import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { getMe, login as apiLogin, logout as apiLogout } from './api'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      const { user: me } = await getMe()
      setUser(me)
      return me
    } catch {
      setUser(null)
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const value = useMemo(
    () => ({
      user,
      loading,
      refresh,
      signIn: async (email, password) => {
        const { user: me } = await apiLogin(email, password)
        setUser(me)
        return me
      },
      signOut: async () => {
        try {
          await apiLogout()
        } finally {
          setUser(null)
        }
      },
    }),
    [user, loading, refresh]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}

export const portalPathFor = (role) =>
  role === 'admin' ? '/portal/admin' : role === 'caregiver' ? '/portal/caregiver' : '/portal/family'

/**
 * Client-side gate for routing and messaging only — the API independently
 * enforces the same rules, so bypassing this reveals nothing.
 */
export function RequireRole({ role, children }) {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div style={{ minHeight: '50vh', display: 'grid', placeItems: 'center', color: 'var(--color-muted)' }}>
        Loading…
      </div>
    )
  }
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />
  if (role && user.role !== role) return <Navigate to={portalPathFor(user.role)} replace />
  return children
}
